//codex-- Use the page-scoped print toolbar presentation.
import PageToolbar from './PageToolbar';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import serviceLogo from '../assets/images/service_logo.png';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ServiceSignatureLocator from '../components/ServiceSignatureLocator';

const WipTicket = () => {
  const [wipReport, setWipReport] = useState(null); // Initialize as null
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [partsData, setPartsData] = useState([]);
  const [hoursData, setHoursData] = useState([]);
  const appRoot = process.env.REACT_APP_ROOT_URL;

  useEffect(() => {
    setLoading(true);
    const fetchServiceCalls = async () => {
      try {
        const response = await axios.get(`${apiUrl}/wip_crud.php?getWipReport=${id}`, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
        });
        if (response.data[0] === 'success') {
          const scheduleData = response.data[1];
          setWipReport(scheduleData);
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          toast.error("Session expired. Please login again.", { position: "top-center" });
          navigate('/login');
        } else if (error.response && error.response.status === 403) {
          toast.error("Oops!! you don't have access to this area. Speak to the CFO.", { position: "top-center" });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchServiceCalls();
  }, [apiUrl, navigate, id]);

  // Handle the printing
  const handlePrint = () => {
    window.print();
  };

  // Loading state
  if (loading) {
    return <div>Loading...</div>;
  }

  // Fallback if no service call data is available
  if (!wipReport) {
    return <div className='p-5'>No data available</div>;
  }

  return (
    <div className="mx-auto bg-white p-6" style={{ width: '8.5in', height: '11in' }}>
      {/* Print Button */}
      <>{/*codex-- Match the toolbar design and hide the complete toolbar when printing. */}
        <PageToolbar title="WIP ticket tools" className="print:hidden" actions={<button onClick={handlePrint} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 print:hidden">Print</button>} />
      </>

      {/* Include the style block for print-specific CSS */}
      <style>
        {`
          @media print {
            /* Hide the print button */
            .print\\:hidden {
              display: none !important;
            }

            /* Hide the menu bar during print */
            .menu {
              display: none !important;
            }

            /* Ensure the main content stretches across the full width of the page */
            .max-w-5xl {
              max-width: 100% !important;
            }

            /* Prevent page breaks inside tables */
            table {
              width: 100%;
              border-collapse: collapse;
              page-break-inside: avoid; /* Prevents splitting the table */
            }

            th, td {
              border: 1px solid black;
              padding: 8px;
              font-size: 12px; /* Optional: Reduce font size for print */
            }

            /* Ensure all text prints in black */
            * {
              color: black !important;
            }

            /* Adjust body size for print if necessary */
            body {
              font-size: 90%; /* Optional: Reduce the overall font size to fit more content */
            }

            /* Set the table width to fit on one page */
            table {
              table-layout: fixed;
            }

            th, td {
              word-wrap: break-word; /* Allow breaking of long words inside cells */
            }
          }
        `}
      </style>

      <h3 className="text-right text-gray-500 text-xl mb-4">Report Job# {wipReport?.job_number}</h3>

      {/* Customer Info */}
      <div className="bg-white px-4 py-5 mb-4 border border-gray-300 rounded-md">
        <div className="flex justify-between items-start">
          {/* Logo */}
          <div>
            <img src={serviceLogo} alt="logo" className="max-w-96" />
          </div>

          {/* Customer Info */}
          <div className="flex-1 ml-auto">
            <div className="min-w-0">
              <div className="flex items-center">
                <p className="text-sm font-semibold text-gray-900 mr-2">Customer:</p>
                <p className="text-sm text-gray-500">{wipReport.customer_name}</p>
              </div>

              <div className="flex items-center mt-2">
                <p className="text-sm font-semibold text-gray-900 mr-2">Address:</p>
                <p className="text-sm text-gray-500">{wipReport.job_site}</p>
              </div>

              <div className="flex items-center mt-2">
                <p className="text-sm font-semibold text-gray-900 mr-2">Type:</p>
                <p className="text-sm text-gray-500">{wipReport.type}</p>
              </div>

              {/* {wipReport.CUSTOMEREMAIL && (
                <div className="flex items-center mt-2">
                  <p className="text-sm font-semibold text-gray-900 mr-2">Sent To:</p>
                  <p className="text-sm text-gray-500">{wipReport.CUSTOMEREMAIL}</p>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>

      {/* Service Details */}
      <div className="flex flex-wrap gap-4 mb-3">
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">Schedule from:</div>
          <div>{wipReport.schedule_from}</div>
        </div>
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">Schedule to:</div>
          <div>{wipReport.schedule_to}</div>
        </div>

        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">COMPLETE:</div>
          <div className={wipReport.status === 'Completed' ? 'text-green-600' : 'text-red-600'}>
            {wipReport.status === 'Completed' ? 'YES' : 'NO'}
          </div>
        </div>
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">TECHNICIAN:</div>
          <div>{wipReport.tech_name}</div>
        </div>
      </div>

      {/* Description of Service */}
      <div className="border border-black rounded-md p-4 mb-6">
        <div className="font-bold underline mb-2">Job Description:</div>
        <div className="text-sm">{wipReport.job_description}</div>
      </div>

      {/* Technician Notes */}
      <div className="border border-black rounded-md p-4 mb-6">
        <div className="font-bold underline mb-2">TECHNICIAN NOTES:</div>
        <div className="text-sm">{wipReport.tech_notes}</div>
      </div>

    </div>
  );
};

export default WipTicket;
